<template>
  <div class="normativa-lista-dashboard">
    <!-- Card de Filtros -->
    <v-card class="filters-card mb-5" flat>
      <v-card-title
          class="d-flex align-center justify-space-between pa-3 filters-header"
          @click="toggleFiltros"
      >
        <div class="d-flex align-center">
          <v-icon color="#023449" :size="isMobile ? 20 : 24" class="mr-2">mdi-filter-variant</v-icon>
          <span class="filters-title">Filtros de Busca</span>
        </div>

        <div class="d-flex align-center">
          <!-- Indicador de Filtros Ativos -->
          <v-chip
              v-if="!filtrosExpandidos && filtrosAtivos"
              x-small
              color="#e8f5e9"
              text-color="#2e7d32"
              class="mr-2"
          >
            <v-icon left x-small color="#2e7d32">mdi-filter</v-icon>
            <span v-if="!isMobile">Filtros ativos</span>
          </v-chip>

          <!-- Ícone Expandir/Recolher -->
          <v-icon :size="isMobile ? 20 : 24" :disabled="carregando">
            {{ filtrosExpandidos ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
          </v-icon>
        </div>
      </v-card-title>

      <!-- Painel de Filtros Expansível -->
      <v-expand-transition>
        <div v-show="filtrosExpandidos">
          <v-divider></v-divider>
          <v-card-text class="pa-3">
            <v-row :class="isMobile ? 'mx-0' : ''">
              <!-- Busca -->
              <v-col cols="12" :md="isMobile ? 12 : 5" class="py-2">
                <v-text-field
                    v-model="filtros.busca"
                    label="Buscar"
                    placeholder="Código ou título..."
                    prepend-inner-icon="mdi-magnify"
                    outlined
                    dense
                    clearable
                    @input="buscarComDebounce"
                    :disabled="carregando"
                    hide-details="auto"
                ></v-text-field>
              </v-col>

              <!-- Categorias (Múltipla Seleção) -->
              <v-col cols="12" :md="isMobile ? 12 : 5" class="py-2">
                <v-select
                    v-model="filtros.ids_categorias"
                    :items="categoriasFiltro"
                    item-text="nome"
                    item-value="id"
                    label="Filtrar por Categorias"
                    placeholder="Todas as categorias"
                    prepend-inner-icon="mdi-shape"
                    outlined
                    dense
                    multiple
                    chips
                    small-chips
                    deletable-chips
                    clearable
                    @change="aplicarFiltros"
                    :disabled="carregando"
                    :loading="carregandoCategorias"
                    hide-details="auto"
                >
                  <!-- Chips Selecionados -->
                  <template v-slot:selection="{ item, index }">
                    <CategoriaChip
                        v-if="index < (isMobile ? 1 : 2)"
                        :categoria="item"
                        size="x-small"
                        class="mr-1"
                    />
                    <span
                        v-if="index === (isMobile ? 1 : 2)"
                        class="grey--text text-caption"
                    >
                      (+{{ filtros.ids_categorias.length - (isMobile ? 1 : 2) }})
                    </span>
                  </template>

                  <!-- Itens do Dropdown -->
                  <template v-slot:item="{ item }">
                    <CategoriaChip
                        :categoria="item"
                        size="small"
                        class="my-1"
                    />
                  </template>
                </v-select>
              </v-col>

              <!-- Botão Limpar -->
              <v-col cols="12" :md="isMobile ? 12 : 2" class="py-2 d-flex align-center" :class="isMobile ? 'justify-center' : 'justify-end'">
                <v-btn
                    text
                    color="error"
                    @click="limparFiltros"
                    :height="isMobile ? 36 : 40"
                    :width="isMobile ? '100%' : '100%'"
                    outlined
                    :disabled="carregando || !filtrosAtivos"
                    :small="isMobile"
                >
                  <v-icon left :small="isMobile">mdi-filter-remove</v-icon>
                  Limpar
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </div>
      </v-expand-transition>
    </v-card>

    <!-- Loading -->
    <div v-if="carregando && normativas.length === 0" class="loading-state">
      <v-progress-circular
          indeterminate
          color="#023449"
          :size="isMobile ? 48 : 64"
      ></v-progress-circular>
      <p class="mt-4 loading-text">Carregando normativas...</p>
    </div>

    <!-- Lista Vazia -->
    <div v-else-if="!carregando && normativas.length === 0" class="no-data-message">
      <v-icon :size="isMobile ? 48 : 64" color="#cbd5e1">
        {{ filtrosAtivos ? 'mdi-file-search-outline' : 'mdi-folder-open-outline' }}
      </v-icon>
      <h3>{{ filtrosAtivos ? 'Nenhuma normativa encontrada' : 'Nenhuma normativa cadastrada' }}</h3>
      <p>{{ filtrosAtivos ? 'Tente ajustar os filtros ou limpar a busca' : 'Crie normativas para organizar os documentos da empresa' }}</p>

      <v-btn
          v-if="!filtrosAtivos && permissao('normativa_admin')"
          color="#023449"
          dark
          outlined
          @click="abrirFormulario()"
          class="mt-4"
          :small="isMobile"
          :block="isMobile"
      >
        <v-icon left :small="isMobile">mdi-plus</v-icon>
        Criar Primeira Normativa
      </v-btn>
      <v-btn
          v-else-if="filtrosAtivos"
          color="#023449"
          dark
          outlined
          @click="limparFiltros"
          class="mt-4"
          :small="isMobile"
          :block="isMobile"
      >
        <v-icon left :small="isMobile">mdi-filter-remove</v-icon>
        Limpar Filtros
      </v-btn>
    </div>

    <!-- Cards Mobile -->
    <div v-else-if="isMobile" class="normativas-cards-mobile">
      <!-- Header Mobile -->
      <v-card class="table-card mb-3" flat>
        <v-card-title class="pa-3 table-header">
          <div class="d-flex align-center flex-grow-1">
            <v-icon color="#023449" size="20" class="mr-2">mdi-archive</v-icon>
            <span class="table-title">Documentos</span>
          </div>
          <v-chip x-small color="#e8f5e9" text-color="#2e7d32" class="ml-2">
            <v-icon left x-small color="#2e7d32">mdi-file-document</v-icon>
            {{ normativas.length }}
          </v-chip>
        </v-card-title>

        <!-- Botão Nova Normativa Mobile -->
        <v-card-actions v-if="permissao('normativa_admin')" class="pa-3 pt-0">
          <v-btn
              color="#023449"
              dark
              block
              small
              @click="abrirFormulario()"
              :disabled="carregando"
          >
            <v-icon left small>mdi-plus</v-icon>
            Nova Normativa
          </v-btn>
        </v-card-actions>
      </v-card>

      <!-- Cards de Normativas -->
      <v-card
          v-for="item in normativas"
          :key="item.id"
          class="normativa-card-mobile mb-3"
          @click="visualizar(item)"
      >
        <v-card-title class="pa-3 pb-2">
          <div class="d-flex align-center justify-space-between" style="width: 100%;">
            <v-chip x-small color="#023449" dark>
              {{ item.codigo }}
            </v-chip>
            <v-chip
                x-small
                :color="item.quantidade_anexos > 0 ? 'success' : 'grey'"
                dark
            >
              <v-icon left x-small>mdi-paperclip</v-icon>
              {{ item.quantidade_anexos || 0 }}
            </v-chip>
          </div>
        </v-card-title>

        <v-card-subtitle class="pa-3 pt-2 pb-2">
          <div class="titulo-mobile font-weight-medium">
            {{ item.titulo }}
          </div>
        </v-card-subtitle>

        <v-card-text class="pa-3 pt-0">
          <!-- Categorias -->
          <div class="d-flex flex-wrap align-center mb-2" style="gap: 4px;">
            <template v-if="item.categorias && item.categorias.length > 0">
              <CategoriaChip
                  v-for="(categoria, index) in item.categorias"
                  :key="categoria.id"
                  v-show="index < 2"
                  :categoria="categoria"
                  size="x-small"
              />
              <v-chip v-if="item.categorias.length > 2" x-small color="grey" dark>
                +{{ item.categorias.length - 2 }}
              </v-chip>
            </template>
            <template v-else>
              <v-chip x-small color="grey" text-color="white">
                Sem categoria
              </v-chip>
            </template>
          </div>

          <!-- Data -->
          <div class="text-caption grey--text">
            Atualizada em {{ formatarDataAtualizacao(item) }}
          </div>
        </v-card-text>

        <!-- Ações Mobile -->
        <v-card-actions class="pa-3 pt-0" @click.stop>
          <v-btn
              v-if="isPDF(item)"
              icon
              small
              @click="visualizarPDF(item)"
              :disabled="processando"
              color="primary"
          >
            <v-icon small>mdi-file-eye</v-icon>
          </v-btn>

          <v-btn
              icon
              small
              @click="downloadArquivo(item)"
              :disabled="processando"
              color="primary"
          >
            <v-icon small>mdi-download</v-icon>
          </v-btn>

          <v-spacer></v-spacer>

          <v-btn
              v-if="permissao('normativa_admin')"
              icon
              small
              @click="editar(item)"
              :disabled="processando"
              color="#023449"
          >
            <v-icon small>mdi-pencil</v-icon>
          </v-btn>

          <v-btn
              v-if="permissao('normativa_admin')"
              icon
              small
              @click="confirmarInativacao(item)"
              :disabled="processando || item.ativo === 'F'"
              color="error"
          >
            <v-icon small>mdi-delete</v-icon>
          </v-btn>
        </v-card-actions>
      </v-card>
    </div>

    <!-- Tabela Desktop -->
    <v-card v-else class="table-card" flat>
      <v-card-title class="pa-4 table-header">
        <v-icon color="#023449" class="mr-2">mdi-archive</v-icon>
        <span class="table-title">Repositório de Documentos</span>
        <v-spacer></v-spacer>
        <v-chip small color="#e8f5e9" text-color="#2e7d32" class="mr-3">
          <v-icon left x-small color="#2e7d32">mdi-file-document</v-icon>
          {{ normativas.length }} {{ normativas.length === 1 ? 'normativa' : 'normativas' }}
        </v-chip>

        <!-- Botão Nova Normativa (apenas admin) -->
        <v-btn
            v-if="permissao('normativa_admin')"
            color="#023449"
            dark
            class="action-btn"
            small
            @click="abrirFormulario()"
            :disabled="carregando"
        >
          <v-icon left small>mdi-plus</v-icon>
          Nova Normativa
        </v-btn>
      </v-card-title>

      <v-divider></v-divider>

      <v-data-table
          :headers="headersComputados"
          :items="normativas"
          :loading="carregando"
          :items-per-page="-1"
          class="normativas-table"
          loading-text="Carregando normativas..."
          no-data-text="Nenhuma normativa encontrada"
          :footer-props="{
            'items-per-page-text': 'Itens por página:',
            'items-per-page-options': [10, 25, 50, 100, { text: 'Todos', value: -1 }]
          }"
      >
        <!-- Linha clicável com múltiplas categorias -->
        <template v-slot:item="{ item }">
          <tr @click="visualizar(item)" class="clickable-row">
            <!-- Código -->
            <td>
              <v-chip small color="#023449" dark class="cursor-pointer">
                {{ item.codigo }}
              </v-chip>
            </td>

            <!-- Título -->
            <td>
              <div class="text-truncate titulo-cell" style="max-width: 500px;">
                <span class="font-weight-medium">{{ item.titulo }}</span>
              </div>
            </td>

            <!-- Múltiplas Categorias -->
            <td>
              <div class="d-flex flex-wrap align-center" style="gap: 4px;">
                <template v-if="item.categorias && item.categorias.length > 0">
                  <CategoriaChip
                      v-for="(categoria, index) in item.categorias"
                      :key="categoria.id"
                      v-show="index < 3"
                      :categoria="categoria"
                      size="x-small"
                  />
                  <v-tooltip v-if="item.categorias.length > 3" bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-chip
                          x-small
                          color="grey"
                          dark
                          v-bind="attrs"
                          v-on="on"
                      >
                        +{{ item.categorias.length - 3 }}
                      </v-chip>
                    </template>
                    <div class="d-flex flex-column" style="gap: 4px;">
                      <CategoriaChip
                          v-for="categoria in item.categorias.slice(3)"
                          :key="categoria.id"
                          :categoria="categoria"
                          size="x-small"
                      />
                    </div>
                  </v-tooltip>
                </template>
                <template v-else>
                  <v-chip x-small color="grey" text-color="white">
                    Sem categoria
                  </v-chip>
                </template>
              </div>
            </td>

            <!-- Anexos -->
            <td class="text-center">
              <v-chip
                  x-small
                  :color="item.quantidade_anexos > 0 ? 'success' : 'grey'"
                  dark
              >
                <v-icon left x-small>mdi-paperclip</v-icon>
                {{ item.quantidade_anexos || 0 }}
              </v-chip>
            </td>

            <!-- Data Atualizada -->
            <td class="text-center">
              <span class="text-caption grey--text text--darken-1">
                {{ formatarDataAtualizacao(item) }}
              </span>
            </td>

            <!-- Ações -->
            <td class="text-right" @click.stop>
              <div class="d-flex justify-end action-buttons" style="gap: 4px;">
                <!-- Visualizar PDF (se for PDF) -->
                <v-tooltip v-if="isPDF(item)" bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                        icon
                        small
                        v-bind="attrs"
                        v-on="on"
                        @click="visualizarPDF(item)"
                        :disabled="processando"
                        color="primary"
                    >
                      <v-icon small>mdi-file-eye</v-icon>
                    </v-btn>
                  </template>
                  <span>Visualizar PDF</span>
                </v-tooltip>

                <!-- Download -->
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                        icon
                        small
                        v-bind="attrs"
                        v-on="on"
                        @click="downloadArquivo(item)"
                        :disabled="processando"
                        color="primary"
                    >
                      <v-icon small>mdi-download</v-icon>
                    </v-btn>
                  </template>
                  <span>Download</span>
                </v-tooltip>

                <!-- Editar (apenas admin) -->
                <v-tooltip v-if="permissao('normativa_admin')" bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                        icon
                        small
                        v-bind="attrs"
                        v-on="on"
                        @click="editar(item)"
                        :disabled="processando"
                        color="#023449"
                    >
                      <v-icon small>mdi-pencil</v-icon>
                    </v-btn>
                  </template>
                  <span>Editar</span>
                </v-tooltip>

                <!-- Inativar (apenas admin) -->
                <v-tooltip v-if="permissao('normativa_admin')" bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                        icon
                        small
                        v-bind="attrs"
                        v-on="on"
                        @click="confirmarInativacao(item)"
                        :disabled="processando || item.ativo === 'F'"
                        color="error"
                    >
                      <v-icon small>mdi-delete</v-icon>
                    </v-btn>
                  </template>
                  <span>Inativar</span>
                </v-tooltip>
              </div>
            </td>
          </tr>
        </template>
      </v-data-table>
    </v-card>

    <!-- Componentes de Dialog -->
    <NormativaFormulario
        v-if="permissao('normativa_admin')"
        :dialog.sync="dialogFormulario"
        :normativa="normativaEditando"
        @salvo="handleNormativaSalva"
        @fechar="handleFormularioFechado"
    />

    <NormativaVisualizacao
        :dialog.sync="dialogVisualizacao"
        :normativa="normativaSelecionada"
        @editar="editarDaVisualizacao"
        @fechar="dialogVisualizacao = false"
    />

    <!-- Dialog de Visualização de PDF -->
    <v-dialog
        v-model="dialogPDF"
        :max-width="isMobile ? '95vw' : '1400px'"
        :fullscreen="isMobile"
        persistent
        no-click-animation
        :retain-focus="false"
    >
      <v-card v-if="pdfSelecionado">
        <v-card-title class="pdf-viewer-header pa-3 white--text">
          <v-icon color="red" :size="isMobile ? 20 : 24" class="mr-2">mdi-file-pdf-box</v-icon>
          <div class="flex-grow-1" style="min-width: 0;">
            <div :class="isMobile ? 'text-body-2' : 'text-subtitle-1'" class="font-weight-medium text-truncate">
              {{ pdfSelecionado.codigo }} - {{ pdfSelecionado.titulo }}
            </div>
            <div class="text-caption grey--text text--lighten-1 text-truncate">
              {{ pdfSelecionado.arquivo_nome }}
            </div>
          </div>

          <!-- Botão Fechar -->
          <v-btn
              icon
              @click="fecharVisualizacaoPDF"
              color="white"
              :small="isMobile"
          >
            <v-icon :size="isMobile ? 20 : 24">mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-0">
          <!-- Loading PDF -->
          <div v-if="carregandoPDF" class="pdf-loading-container">
            <v-progress-circular
                indeterminate
                color="#023449"
                :size="isMobile ? 48 : 64"
            ></v-progress-circular>
            <p class="mt-4 text-subtitle-1">Carregando PDF...</p>
          </div>

          <!-- Erro ao carregar PDF -->
          <div v-else-if="erroPDF" class="pdf-loading-container">
            <v-icon color="error" :size="isMobile ? 48 : 64">mdi-alert-circle</v-icon>
            <p :class="isMobile ? 'text-subtitle-1' : 'text-h6'" class="mt-4 error--text">Erro ao carregar PDF</p>
            <p class="text-body-2 grey--text px-4 text-center">{{ erroPDF }}</p>
            <v-btn
                color="#023449"
                dark
                class="mt-4"
                @click="downloadArquivo(pdfSelecionado)"
                :small="isMobile"
            >
              <v-icon left :small="isMobile">mdi-download</v-icon>
              Baixar Arquivo
            </v-btn>
          </div>

          <!-- Visualizador PDF -->
          <iframe
              v-else-if="pdfUrl"
              :src="pdfUrl"
              class="pdf-viewer-iframe"
              frameborder="0"
              title="Visualizador de PDF"
          ></iframe>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Dialog de Confirmação -->
    <v-dialog
        v-if="permissao('normativa_admin')"
        v-model="dialogConfirmacao"
        :max-width="isMobile ? '95vw' : '500px'"
        persistent
    >
      <v-card>
        <v-card-title :class="isMobile ? 'text-subtitle-1' : 'text-h6'">
          <v-icon left color="warning" :size="isMobile ? 20 : 24">mdi-alert</v-icon>
          Confirmar Inativação
        </v-card-title>

        <v-card-text class="pb-0">
          <p :class="isMobile ? 'text-body-2' : ''">
            Tem certeza que deseja inativar a normativa
            <strong>{{ normativaSelecionada?.codigo }} - {{ normativaSelecionada?.titulo }}</strong>?
          </p>
          <v-alert type="warning" dense text class="mt-2 mb-0">
            <span :class="isMobile ? 'text-caption' : 'text-body-2'">
              Esta ação pode ser revertida posteriormente editando a normativa.
            </span>
          </v-alert>
        </v-card-text>

        <v-card-actions class="pa-3">
          <v-btn text @click="dialogConfirmacao = false" :disabled="processando" :small="isMobile">
            Cancelar
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn color="error" @click="inativarNormativa" :loading="processando" :small="isMobile">
            <v-icon left :small="isMobile">mdi-delete</v-icon>
            Inativar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar
        v-model="snackbar.show"
        :color="snackbar.color"
        :timeout="3000"
        bottom
        :multi-line="isMobile"
    >
      {{ snackbar.message }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false" :small="isMobile">
          Fechar
        </v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import axios from 'axios'
import NormativaFormulario from './NormativaFormulario.vue'
import NormativaVisualizacao from './NormativaVisualizacao.vue'
import CategoriaChip from '../Widgets/CategoriaChip.vue'
import { mapState } from 'vuex'

export default {
  name: 'NormativaLista',

  components: {
    NormativaFormulario,
    NormativaVisualizacao,
    CategoriaChip
  },

  data() {
    return {
      normativas: [],
      categorias: [],
      carregando: false,
      carregandoCategorias: false,
      processando: false,
      dialogFormulario: false,
      dialogVisualizacao: false,
      dialogConfirmacao: false,
      dialogPDF: false,
      carregandoPDF: false,
      erroPDF: null,
      pdfUrl: null,
      pdfSelecionado: null,
      normativaEditando: null,
      normativaSelecionada: null,
      debounceTimer: null,
      filtrosExpandidos: true,
      windowWidth: window.innerWidth,

      filtros: {
        busca: '',
        ids_categorias: []
      },

      headers: [
        { text: 'Nº', value: 'codigo', width: '90px', align: 'center' },
        { text: 'Título', value: 'titulo', sortable: true },
        { text: 'Categorias', value: 'categorias', width: '280px' },
        { text: 'Anexos', value: 'quantidade_anexos', width: '120px', align: 'center' },
        { text: 'Atualizada em', value: 'data_atualizacao', width: '160px', align: 'center' },
        { text: 'Ações', value: 'acoes', width: '180px', align: 'end', sortable: false }
      ],

      snackbar: {
        show: false,
        message: '',
        color: 'success'
      }
    }
  },

  computed: {
    ...mapState(["backendUrl"]),

    isMobile() {
      return this.windowWidth < 960
    },

    categoriasFiltro() {
      return this.categorias
    },

    filtrosAtivos() {
      return !!(
          this.filtros.busca ||
          (this.filtros.ids_categorias && Array.isArray(this.filtros.ids_categorias) && this.filtros.ids_categorias.length > 0)
      )
    },

    headersComputados() {
      if (!this.permissao('normativa_admin')) {
        return this.headers.filter(h => h.value !== 'ativo')
      }
      return this.headers
    }
  },

  mounted() {
    this.carregarCategorias()
    this.carregarNormativas()

    const filtrosExpandidosStorage = localStorage.getItem('normativas_filtros_expandidos')
    if (filtrosExpandidosStorage !== null) {
      this.filtrosExpandidos = filtrosExpandidosStorage === 'true'
    }

    // Listener para redimensionamento
    window.addEventListener('resize', this.handleResize)
  },

  beforeDestroy() {
    // Limpar URL do blob ao destruir o componente
    if (this.pdfUrl) {
      window.URL.revokeObjectURL(this.pdfUrl)
    }

    // Remover listener
    window.removeEventListener('resize', this.handleResize)
  },

  watch: {
    filtrosExpandidos(val) {
      localStorage.setItem('normativas_filtros_expandidos', val)
    },

    dialogPDF(val) {
      if (!val) {
        this.limparPDF()
      }

      // Controla o overflow do body
      this.$nextTick(() => {
        if (val) {
          document.body.style.overflow = 'hidden'
          // Remove scroll de outros diálogos
          const dialogs = document.querySelectorAll('.v-dialog--active')
          dialogs.forEach(dialog => {
            if (dialog !== document.querySelector('.v-dialog--active:last-child')) {
              dialog.style.overflowY = 'hidden'
            }
          })
        } else {
          document.body.style.overflow = ''
        }
      })
    }
  },

  methods: {
    handleResize() {
      this.windowWidth = window.innerWidth
    },

    async carregarCategorias() {
      this.carregandoCategorias = true

      try {
        const response = await axios.post(`${this.backendUrl}normativa/categoria`, {
          acao: 'listar'
        })

        this.categorias = response.data || []

      } catch (error) {
        console.error('Erro ao carregar categorias:', error)
      } finally {
        this.carregandoCategorias = false
      }
    },

    async carregarNormativas() {
      this.carregando = true

      try {
        const payload = {
          acao: 'listar'
        }

        if (this.filtros.ids_categorias && Array.isArray(this.filtros.ids_categorias) && this.filtros.ids_categorias.length > 0) {
          payload.ids_categorias = this.filtros.ids_categorias
        }

        if (this.filtros.busca && this.filtros.busca.trim()) {
          payload.busca = this.filtros.busca.trim()
        }

        const response = await axios.post(`${this.backendUrl}normativa`, payload)

        this.normativas = response.data || []

      } catch (error) {
        console.error('Erro ao carregar normativas:', error)

        const mensagemErro = error.response?.data?.erro
            || error.message
            || 'Erro ao carregar normativas'

        this.mostrarSnackbar(mensagemErro, 'error')
      } finally {
        this.carregando = false
      }
    },

    buscarComDebounce() {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = setTimeout(() => {
        this.aplicarFiltros()
      }, 500)
    },

    toggleFiltros() {
      if (!this.carregando) {
        this.filtrosExpandidos = !this.filtrosExpandidos
      }
    },

    aplicarFiltros() {
      this.carregarNormativas()
    },

    limparFiltros() {
      this.filtros.busca = ''
      this.filtros.ids_categorias = []
      this.carregarNormativas()
    },

    abrirFormulario(normativa = null) {
      this.normativaEditando = normativa
      this.dialogFormulario = true
    },

    async visualizar(normativa) {
      try {
        const response = await axios.post(`${this.backendUrl}normativa`, {
          acao: 'buscar',
          id: normativa.id
        })

        this.normativaSelecionada = response.data
        this.dialogVisualizacao = true

      } catch (error) {
        console.error('Erro ao carregar normativa:', error)
        this.mostrarSnackbar('Erro ao carregar detalhes da normativa', 'error')
      }
    },

    editar(normativa) {
      this.visualizar(normativa).then(() => {
        this.dialogVisualizacao = false
        this.abrirFormulario(this.normativaSelecionada)
      })
    },

    editarDaVisualizacao(normativa) {
      if (!this.permissao('normativa_admin')) {
        this.mostrarSnackbar('Você não tem permissão para editar normativas', 'error')
        return
      }

      this.dialogVisualizacao = false
      this.abrirFormulario(normativa)
    },

    isPDF(normativa) {
      if (!normativa || !normativa.arquivo_nome) return false

      const nomeArquivo = normativa.arquivo_nome.toLowerCase()
      return nomeArquivo.endsWith('.pdf')
    },

    async visualizarPDF(normativa) {
      if (!this.isPDF(normativa)) {
        this.mostrarSnackbar('Apenas arquivos PDF podem ser visualizados', 'warning')
        return
      }

      this.pdfSelecionado = normativa
      this.dialogPDF = true
      this.carregandoPDF = true
      this.erroPDF = null

      try {
        const response = await axios.post(`${this.backendUrl}normativa/download`, {
          codigo: normativa.codigo,
          arquivo_nome: normativa.arquivo_nome,
          tipo_arquivo: 'principal'
        }, {
          responseType: 'blob'
        })

        const blob = new Blob([response.data], { type: 'application/pdf' })
        const url = window.URL.createObjectURL(blob)

        // Parâmetros para o visualizador de PDF
        const pdfParams = [
          'navpanes=1',
          'pagemode=thumbs',
          'page=1'
        ].join('&')

        this.pdfUrl = `${url}#${pdfParams}`

      } catch (error) {
        console.error('Erro ao carregar PDF:', error)

        this.erroPDF = error.response?.data?.erro
            || error.response?.statusText
            || 'Não foi possível carregar o arquivo PDF'

      } finally {
        this.carregandoPDF = false
      }
    },

    fecharVisualizacaoPDF() {
      this.dialogPDF = false
    },

    limparPDF() {
      if (this.pdfUrl) {
        window.URL.revokeObjectURL(this.pdfUrl)
        this.pdfUrl = null
      }
      this.pdfSelecionado = null
      this.erroPDF = null
      this.carregandoPDF = false
    },

    async downloadArquivo(normativa) {
      try {
        const response = await axios.post(`${this.backendUrl}normativa/download`, {
          codigo: normativa.codigo,
          arquivo_nome: normativa.arquivo_nome,
          tipo_arquivo: 'principal'
        }, {
          responseType: 'blob'
        })

        const nomeArquivo = normativa.arquivo_nome || `${normativa.codigo}_normativa.pdf`

        const url = window.URL.createObjectURL(new Blob([response.data]))
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', nomeArquivo)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        window.URL.revokeObjectURL(url)

        this.mostrarSnackbar('Download concluído!', 'success')

      } catch (error) {
        console.error('Erro ao fazer download:', error)

        const mensagemErro = error.response?.data?.erro
            || error.response?.statusText
            || 'Erro ao fazer download do arquivo'

        this.mostrarSnackbar(mensagemErro, 'error')
      }
    },

    confirmarInativacao(normativa) {
      this.normativaSelecionada = normativa
      this.dialogConfirmacao = true
    },

    async inativarNormativa() {
      if (!this.normativaSelecionada) return

      this.processando = true

      try {
        await axios.post(`${this.backendUrl}normativa`, {
          acao: 'deletar',
          id: this.normativaSelecionada.id
        })

        this.mostrarSnackbar('Normativa inativada com sucesso!', 'success')
        this.dialogConfirmacao = false
        this.normativaSelecionada = null
        this.carregarNormativas()

      } catch (error) {
        console.error('Erro ao inativar normativa:', error)

        const mensagemErro = error.response?.data?.erro
            || error.message
            || 'Erro ao inativar normativa'

        this.mostrarSnackbar(mensagemErro, 'error')
      } finally {
        this.processando = false
      }
    },

    handleNormativaSalva() {
      this.carregarNormativas()
    },

    handleFormularioFechado() {
      this.normativaEditando = null
    },

    formatarDataAtualizacao(item) {
      const dataString = item.data_alteracao || item.data_criacao

      if (!dataString) return '-'

      try {
        const partes = dataString.split('-')

        if (partes.length >= 4) {
          const ano = partes[0]
          const mes = partes[1]
          const dia = partes[2]

          const horaCompleta = partes[3]
          const partesHora = horaCompleta.split('.')

          const hora = partesHora[0] || '00'
          const minuto = partesHora[1] || '00'

          const data = new Date(ano, mes - 1, dia, hora, minuto)

          return data.toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
          })
        }

        return '-'
      } catch (error) {
        console.error('Erro ao formatar data:', error, dataString)
        return '-'
      }
    },

    mostrarSnackbar(message, color) {
      this.snackbar.message = message
      this.snackbar.color = color
      this.snackbar.show = true
    }
  }
}
</script>

<style scoped>
/* PDF Viewer Dialog - Estilo NormativaVisualizacao */
.pdf-viewer-header {
  background: rgba(2, 52, 73, 0.9) !important;
  border-bottom: 1px solid whitesmoke;
}

/* Botões de Ação */
.action-buttons {
  display: flex;
  gap: 4px;
}

.normativa-lista-dashboard {
  font-family: 'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
}

/* Cards Styling */
.filters-card,
.table-card {
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(2, 52, 73, 0.04);
  border: 1px solid #e2e8f0;
}

.filters-title,
.table-title {
  font-size: 1.125rem;
  font-weight: 700;
  color: #023449;
  letter-spacing: -0.01em;
}

.filters-header {
  cursor: pointer;
  user-select: none;
  transition: background-color 0.2s ease;
}

.filters-header:hover {
  background-color: rgba(2, 52, 73, 0.02);
}

.filters-header:active {
  background-color: rgba(2, 52, 73, 0.04);
}

/* Action Button */
.action-btn {
  text-transform: none;
  font-weight: 600;
  letter-spacing: 0;
  border-radius: 8px;
  padding: 0 16px !important;
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
}

.loading-text {
  color: #64748b;
  font-size: 0.875rem;
  margin: 0;
}

/* No Data Message */
.no-data-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  gap: 16px;
  padding: 40px;
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
}

.no-data-message h3 {
  margin: 0;
  color: #023449;
  font-size: 1.25rem;
  font-weight: 700;
}

.no-data-message p {
  color: #64748b;
  font-size: 0.875rem;
  margin: 0;
  text-align: center;
}

/* Table Styling */
.table-header {
  background-color: #fafafa;
}

::v-deep .normativas-table {
  font-family: inherit;
}

::v-deep .normativas-table .v-data-table-header {
  background-color: #fafafa;
}

::v-deep .normativas-table th {
  color: #023449 !important;
  font-weight: 700 !important;
  font-size: 0.813rem !important;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Linha Clicável */
.clickable-row {
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.clickable-row:hover {
  background-color: rgba(2, 52, 73, 0.04) !important;
}

.titulo-cell {
  color: #1e293b;
  transition: color 0.2s ease;
}

.clickable-row:hover .titulo-cell {
  color: #023449;
}

.cursor-pointer {
  cursor: pointer;
}

.normativas-cards-mobile {
  display: flex;
  flex-direction: column;
}

.normativa-card-mobile {
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(2, 52, 73, 0.04);
  border: 1px solid #e2e8f0;
  cursor: pointer;
  transition: all 0.2s ease;
}

.normativa-card-mobile:hover,
.normativa-card-mobile:active {
  box-shadow: 0 4px 6px rgba(2, 52, 73, 0.08);
  transform: translateY(-1px);
}

/* Ajusta o espaçamento interno do card-subtitle */
.normativa-card-mobile .v-card__subtitle {
  padding-top: 8px !important;
  line-height: 1.4;
}

.titulo-mobile {
  color: #1e293b;
  font-size: 0.938rem;
  line-height: 1.5;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
  margin-top: 4px;
}

/* Previne scroll duplo em diálogos */
::v-deep .v-dialog--active {
  overflow-y: hidden !important;
}

/* Garante que apenas o conteúdo do diálogo tenha scroll quando necessário */
::v-deep .v-dialog--active .v-card {
  overflow: visible;
}

/* O iframe do PDF deve gerenciar seu próprio scroll */
.pdf-viewer-iframe {
  width: 100%;
  height: calc(100vh - 200px);
  min-height: 600px;
  background-color: #525659;
  overflow: auto;
}

/* Remove scroll da área de loading */
.pdf-loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 600px;
  background-color: #f5f5f5;
  overflow: hidden;
  padding: 20px;
}

/* Responsive Mobile */
@media (max-width: 959px) {
  .filters-card,
  .table-card {
    border-radius: 8px;
    margin-left: 8px;
    margin-right: 8px;
  }

  .normativa-lista-dashboard {
    padding: 0;
  }

  .filters-title,
  .table-title {
    font-size: 0.938rem;
  }

  .action-btn {
    padding: 0 12px !important;
  }

  ::v-deep .normativas-table th {
    font-size: 0.75rem !important;
  }

  .no-data-message {
    min-height: 300px;
    padding: 24px 16px;
  }

  .no-data-message h3 {
    font-size: 1.063rem;
  }

  .no-data-message p {
    font-size: 0.813rem;
  }

  .loading-state {
    min-height: 300px;
  }

  /* PDF Viewer Mobile */
  .pdf-viewer-iframe {
    height: calc(100vh - 120px);
    min-height: auto;
  }

  .pdf-loading-container {
    height: calc(100vh - 120px);
    min-height: 400px;
  }

  /* Cards Mobile Específico */
  .normativa-card-mobile {
    border-radius: 8px;
    margin-left: 8px;
    margin-right: 8px;
  }
}

/* Tablets */
@media (max-width: 768px) and (min-width: 600px) {
  .filters-card,
  .table-card,
  .normativa-card-mobile {
    margin-left: 12px;
    margin-right: 12px;
  }
}

/* Accessibility */
@media (prefers-reduced-motion: reduce) {
  .clickable-row,
  .titulo-cell,
  .normativa-card-mobile {
    transition: none !important;
  }
}

/* Fix para overflow em v-select no mobile */
::v-deep .v-select__selections {
  flex-wrap: nowrap !important;
  overflow: hidden;
}

::v-deep .v-select__selection--comma {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>